import { getFullName } from "@/entities/people/helpers";
import { PersonData } from "@/entities/people/types";
import { EntityData, Filters } from "@/services/api/types";
import { useEntitySearch } from "@/services/hooks/entities/useEntitySearch";
import {
  EntityType,
  getEntityHook
} from "@/services/hooks/entities/useEntityType";
import { isEmpty } from "lodash";
import { useCallback, useMemo, useState } from "react";

export const getMultiSelectOption = (
  entity: EntityData,
  entityType: EntityType,
  isHidden: boolean = false
) => {
  let displayName = "";

  if (entityType === EntityType.USER) {
    displayName = entity.displayName || "";
  } else if (entityType === EntityType.PERSON) {
    displayName = getFullName(entity as PersonData);
  } else {
    displayName = entity?.name || "";
  }

  return {
    label: displayName,
    value: entity.id,
    isHidden: isHidden,
    userData: entityType === EntityType.USER ? entity : undefined
  };
};

interface UseEntityMultiSelectProps {
  entityType: EntityType;
  selectedIds?: string[];
  filters?: Filters;
}

export const useEntityMultiSelect = ({
  entityType,
  selectedIds = [],
  filters
}: UseEntityMultiSelectProps) => {
  const stableSelectedIds = useMemo(
    () => selectedIds,
    [JSON.stringify(selectedIds)]
  );

  const useEntityQuery = getEntityHook(entityType);
  const entityQuery = useEntityQuery
    ? useEntityQuery(
        entityType && stableSelectedIds.length > 0 ? stableSelectedIds : []
      )
    : { entities: [], isSuccess: false };

  const { entities: selectedEntities = [] } = entityQuery;

  const [searchTerm, setSearchTerm] = useState<string>("");

  const { options: searchedOptions, isLoading: isLoadingOptions } =
    useEntitySearch({
      entityType,
      searchTerm,
      filters
    });

  const options = useMemo(() => {
    const searchOptions = searchedOptions.map((option) => ({
      label: option.displayName || "",
      value: option.id as string
    }));

    const combinedOptions = [...searchOptions];

    // If we have selected entities, add them to the top of options if not already there
    if (selectedEntities.length > 0 && stableSelectedIds.length > 0) {
      stableSelectedIds.forEach((selectedId) => {
        const existingInOptions = combinedOptions.some(
          (option) => option.value === selectedId
        );

        if (!existingInOptions) {
          const selectedEntity = selectedEntities.find(
            (entity) => entity?.id === selectedId
          );
          if (selectedEntity) {
            combinedOptions.unshift(
              getMultiSelectOption(
                selectedEntity,
                entityType,
                !isEmpty(searchTerm)
              )
            );
          }
        }
      });
    }

    return combinedOptions;
  }, [
    selectedEntities,
    searchedOptions,
    stableSelectedIds,
    searchTerm,
    entityType
  ]);

  const onSearch = useCallback((newSearchVal: string) => {
    setSearchTerm(newSearchVal);
  }, []);

  return {
    options,
    isLoading: isLoadingOptions,
    onSearch,
    searchTerm,
    setSearchTerm
  };
};
